import type {
  GameDefinition,
  HighlightsSummaryDefinition,
  LeaderboardSummaryDefinition,
  QuizOptionDefinition,
  QuizRoundDefinition,
  RoundDefinition,
  SummaryDefinition,
  VoteRoundDefinition,
} from "./types";

export type GameDefinitionValidation =
  | { success: true; data: GameDefinition }
  | { success: false; errors: string[] };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function parseQuizOption(
  value: unknown,
  path: string,
  errors: string[],
): QuizOptionDefinition | null {
  if (!isRecord(value)) {
    errors.push(`${path} måste vara ett objekt.`);
    return null;
  }

  if (!isNonEmptyString(value.id) || !isNonEmptyString(value.label)) {
    errors.push(`${path} måste ha id och label.`);
    return null;
  }

  return { id: value.id, label: value.label };
}

function parseRound(
  value: unknown,
  index: number,
  errors: string[],
): RoundDefinition | null {
  const path = `rounds[${index}]`;

  if (!isRecord(value)) {
    errors.push(`${path} måste vara ett objekt.`);
    return null;
  }

  if (!isNonEmptyString(value.id) || !isNonEmptyString(value.prompt)) {
    errors.push(`${path} måste ha id och prompt.`);
    return null;
  }

  if (value.type === "quiz") {
    if (!Array.isArray(value.options) || value.options.length < 2) {
      errors.push(`${path}.options måste innehålla minst två svar.`);
      return null;
    }

    const options = value.options
      .map((option, optionIndex) =>
        parseQuizOption(option, `${path}.options[${optionIndex}]`, errors),
      )
      .filter((option): option is QuizOptionDefinition => option !== null);

    const optionIds = new Set(options.map((option) => option.id));

    if (optionIds.size !== options.length) {
      errors.push(`${path}.options måste ha unika id:n.`);
    }

    if (
      !isNonEmptyString(value.correctOptionId) ||
      !optionIds.has(value.correctOptionId)
    ) {
      errors.push(`${path}.correctOptionId måste peka på ett svarsalternativ.`);
      return null;
    }

    if (
      typeof value.maxPoints !== "number" ||
      !Number.isFinite(value.maxPoints) ||
      value.maxPoints <= 0
    ) {
      errors.push(`${path}.maxPoints måste vara ett positivt tal.`);
      return null;
    }

    return {
      id: value.id,
      type: "quiz",
      prompt: value.prompt,
      options,
      correctOptionId: value.correctOptionId,
      maxPoints: value.maxPoints,
    } satisfies QuizRoundDefinition;
  }

  if (value.type === "vote") {
    if (value.target !== "participant") {
      errors.push(`${path}.target måste vara "participant".`);
      return null;
    }

    return {
      id: value.id,
      type: "vote",
      prompt: value.prompt,
      target: "participant",
    } satisfies VoteRoundDefinition;
  }

  errors.push(`${path}.type måste vara "quiz" eller "vote".`);
  return null;
}

function parseSummary(
  value: unknown,
  errors: string[],
): SummaryDefinition | null {
  if (!isRecord(value)) {
    errors.push("summary måste vara ett objekt.");
    return null;
  }

  if (!isNonEmptyString(value.title) || !isNonEmptyString(value.subtitle)) {
    errors.push("summary måste ha title och subtitle.");
    return null;
  }

  if (value.type === "leaderboard") {
    return {
      type: "leaderboard",
      title: value.title,
      subtitle: value.subtitle,
    } satisfies LeaderboardSummaryDefinition;
  }

  if (value.type === "highlights") {
    return {
      type: "highlights",
      title: value.title,
      subtitle: value.subtitle,
    } satisfies HighlightsSummaryDefinition;
  }

  errors.push('summary.type måste vara "leaderboard" eller "highlights".');
  return null;
}

export function validateGameDefinition(
  value: unknown,
): GameDefinitionValidation {
  const errors: string[] = [];

  if (!isRecord(value)) {
    return { success: false, errors: ["Speldefinitionen måste vara ett objekt."] };
  }

  if (value.schemaVersion !== 1) {
    errors.push("schemaVersion måste vara 1.");
  }

  if (!isNonEmptyString(value.gameId)) {
    errors.push("gameId måste vara en sträng.");
  }

  if (!Array.isArray(value.rounds) || value.rounds.length === 0) {
    errors.push("rounds måste innehålla minst en runda.");
  }

  const rounds = Array.isArray(value.rounds)
    ? value.rounds
        .map((round, index) => parseRound(round, index, errors))
        .filter((round): round is RoundDefinition => round !== null)
    : [];
  const summary = parseSummary(value.summary, errors);

  if (errors.length > 0 || !isNonEmptyString(value.gameId) || !summary) {
    return { success: false, errors };
  }

  return {
    success: true,
    data: {
      schemaVersion: 1,
      gameId: value.gameId,
      rounds,
      summary,
    },
  };
}
